import { cloneDeep } from 'lodash';
import { useIntl } from 'react-intl';
import { Checkbox, Selector, type SelectorOptionPropsInterface } from 'sonata-react-components';
import { checkUserResourcePermissions } from 'sonata-static-storage/lib/helpers/user.helpers';
import { MediaQualityDefaultValue } from 'sonata-static-storage/lib/schemas/targeting/MediaQuality/MediaQuality.schema';

import { useTargetingContext } from '@/contexts/Targeting/TargetingContext';
import { FormExpandableSection } from '../../common/FormExpandableSection';
import { DO_NOT_DISTURB_TARGETING_SECTION_KEY } from '../common/common';
import { SelectionableSectionWithConfirmation } from '../common/SelectionableSectionWithConfirmation';
import GoalPacingDistribution from './components/GoalPacingDistribution';
import HelpTooltip from './components/HelpTooltip';
import { getPermittedMediaQuality } from './helpers/getPermittedMediaQuality.helper';
import { getMediaQualityTags } from './helpers/getSectionHeaderTags.helper';
import './mediaQualityAssuranceSystem.scss';

function MediaQualityAssuranceSystem() {
  const { formatMessage } = useIntl();
  const { budget, currency, setMediaQualityProps, setUnknownIncluded, targeting, resetSection, user, userRoles } =
    useTargetingContext();

  const { mediaQuality } = targeting;

  const unknownIncludedPermission = checkUserResourcePermissions('unknownIncluded', user, userRoles);
  const attentionMetricsPermission = checkUserResourcePermissions('mediaQualityAttentionMetrics', user, userRoles);
  const brandSafetyPermission = checkUserResourcePermissions('brandSafety', user, userRoles);
  const adFraudPermission = checkUserResourcePermissions('adFraud', user, userRoles);
  const viewabilityThresholdPermission = checkUserResourcePermissions('viewabilityThreshold', user, userRoles);
  const viewabilityPixelProviderPermission = checkUserResourcePermissions('viewabilityPixelProvider', user, userRoles);
  const viewabilityAdvanceThresholdPermission = checkUserResourcePermissions(
    'viewabilityAdvanceThreshold',
    user,
    userRoles
  );
  // Like legacy: hide post bid IAS when the user has no enabled viewability pixel providers (unless the campaign already uses one)
  const hasViewabilityPixelProviders =
    user.bidThirdPartyProviders?.some(({ enable }) => enable) ||
    mediaQuality.viewabilityMeasurement.pixelProviders?.some(({ enable }) => enable);

  const safetyLevels = [
    {
      id: 'moderate',
      label: formatMessage({
        defaultMessage: 'Moderate',
        id: 'targeting.mediaQualityAssuranceSystem.options.moderate'
      })
    },
    {
      id: 'high',
      label: formatMessage({
        defaultMessage: 'High',
        id: 'targeting.mediaQualityAssuranceSystem.options.high'
      })
    }
  ];

  const notAppearingOptions = [
    {
      id: 'adult',
      label: formatMessage({
        defaultMessage: 'Adult',
        id: 'targeting.mediaQualityAssuranceSystem.options.adult'
      })
    },
    {
      id: 'alcohol',
      label: formatMessage({
        defaultMessage: 'Alcohol',
        id: 'targeting.mediaQualityAssuranceSystem.options.alcohol'
      })
    },
    {
      id: 'gambling',
      label: formatMessage({
        defaultMessage: 'Gambling',
        id: 'targeting.mediaQualityAssuranceSystem.options.gambling'
      })
    },
    {
      id: 'hate_speech',
      label: formatMessage({
        defaultMessage: 'Hate speech',
        id: 'targeting.mediaQualityAssuranceSystem.options.hate_speech'
      })
    },
    {
      id: 'illegal_downloads',
      label: formatMessage({
        defaultMessage: 'Illegal downloads',
        id: 'targeting.mediaQualityAssuranceSystem.options.illegal_downloads'
      })
    },
    {
      id: 'illegal_drugs',
      label: formatMessage({
        defaultMessage: 'Illegal drugs',
        id: 'targeting.mediaQualityAssuranceSystem.options.illegal_drugs'
      })
    },
    {
      id: 'offensive_language',
      label: formatMessage({
        defaultMessage: 'Offensive language',
        id: 'targeting.mediaQualityAssuranceSystem.options.offensive_language'
      })
    },
    {
      id: 'violence',
      label: formatMessage({
        defaultMessage: 'Violence',
        id: 'targeting.mediaQualityAssuranceSystem.options.violence'
      })
    }
  ];

  const segmentsOptions = [
    {
      id: 20,
      label: formatMessage({
        defaultMessage: '20%',
        id: 'targeting.mediaQualityAssuranceSystem.options.20'
      })
    },
    {
      id: 30,
      label: formatMessage({
        defaultMessage: '30%',
        id: 'targeting.mediaQualityAssuranceSystem.options.30'
      })
    },
    {
      id: 40,
      label: formatMessage({
        defaultMessage: '40%',
        id: 'targeting.mediaQualityAssuranceSystem.options.40'
      })
    },
    {
      id: 50,
      label: formatMessage({
        defaultMessage: '50%',
        id: 'targeting.mediaQualityAssuranceSystem.options.50'
      })
    },
    {
      id: 60,
      label: formatMessage({
        defaultMessage: '60%',
        id: 'targeting.mediaQualityAssuranceSystem.options.60'
      })
    },
    {
      id: 70,
      label: formatMessage({
        defaultMessage: '70%',
        id: 'targeting.mediaQualityAssuranceSystem.options.70'
      })
    }
  ];

  const PreBidOptions = [
    {
      id: 'disabled',
      label: formatMessage({
        defaultMessage: 'Disabled',
        id: 'targeting.mediaQualityAssuranceSystem.options.disabled'
      })
    },
    {
      id: 'global',
      label: formatMessage({
        defaultMessage: 'Use global',
        id: 'targeting.mediaQualityAssuranceSystem.options.global'
      })
    },
    ...segmentsOptions.map(item => ({
      ...item,
      id: item.id.toString()
    }))
  ];

  const getPreBidValue = (index: number): SelectorOptionPropsInterface[] => {
    return !mediaQuality.viewabilityPrebid.thresholds.providers[index].enabled
      ? PreBidOptions.filter(item => item.id === 'disabled')
      : mediaQuality.viewabilityPrebid.thresholds.providers[index].useGlobal
        ? PreBidOptions.filter(item => item.id === 'global')
        : PreBidOptions.filter(
            item => item.id === mediaQuality.viewabilityPrebid.thresholds.providers[index].threshold?.toString()
          );
  };

  const handleSectionEnableChange = (section: keyof typeof MediaQualityDefaultValue, enabled: boolean) => {
    const defaultValue = structuredClone(MediaQualityDefaultValue[section]) as Record<string, any>;
    if (section === 'viewabilityMeasurement' && enabled) {
      defaultValue.pixelProviders = [
        {
          enable: enabled,
          provider: 'ias'
        }
      ];
    }
    if (section === 'attentionRateMeasurement' && enabled) {
      defaultValue.pixelProviders = [
        {
          enable: enabled,
          provider: 'lumen'
        }
      ];
    }
    const isMediaQualityEnabled = Object.keys(mediaQuality)
      .filter(key => key !== 'enabled')
      .reduce((acc, key) => {
        if (key === section) {
          return enabled;
        }
        if ((mediaQuality[key as keyof typeof mediaQuality] as Record<string, any> | undefined)?.enabled) {
          return true;
        }
        return acc;
      }, false);
    setMediaQualityProps(
      section,
      {
        ...defaultValue,
        enabled: enabled
      },
      isMediaQualityEnabled
    );
  };

  const handlePreBidChange = (id: string, index: number) => {
    const providers = cloneDeep(mediaQuality.viewabilityPrebid.thresholds.providers);
    if (id === 'global') {
      providers[index].enabled = true;
      providers[index].useGlobal = true;
      providers[index].threshold = null;
    } else if (id === 'disabled') {
      providers[index].enabled = false;
      providers[index].useGlobal = false;
      providers[index].threshold = null;
    } else {
      providers[index].enabled = true;
      providers[index].useGlobal = false;
      providers[index].threshold = parseInt(id);
    }
    setMediaQualityProps('viewabilityPrebid', {
      ...mediaQuality.viewabilityPrebid,
      thresholds: {
        ...mediaQuality.viewabilityPrebid.thresholds,
        providers
      }
    });
  };

  const handleResetMediaQuality = () => {
    resetSection('mediaQualityAssuranceSystem');
  };

  // Sections enabled by someone with more permissions must not show up for this user
  const permittedMediaQuality = getPermittedMediaQuality(mediaQuality, user, userRoles);
  const isSectionOpened = Boolean(permittedMediaQuality?.enabled);
  const mediaQualityTags = getMediaQualityTags(permittedMediaQuality);

  return (
    <div className="mediaQualityAssuranceSystem" id="mediaQualityAssuranceSystemBox">
      <FormExpandableSection
        defaultOpen={isSectionOpened}
        onReset={handleResetMediaQuality}
        preffixId="mediaQualityAssurance"
        tags={mediaQualityTags}
        title={formatMessage({
          defaultMessage: 'Media Quality Assurance System',
          id: 'targeting.mediaQualityAssuranceSystem.title'
        })}
        tooltipText={formatMessage({
          defaultMessage:
            'Apply third-party brand safety filters, viewability thresholds, and anti-fraud measures to protect your brand.',
          id: 'targeting.mediaQualityAssuranceSystem.tooltip'
        })}
      >
        {brandSafetyPermission && (
          <div className="mediaQualityAssuranceSystem__section">
            <SelectionableSectionWithConfirmation
              id="brandSafetyAssurance"
              isSectionEnabled={mediaQuality.brandSafety.enabled}
              label={formatMessage({
                defaultMessage: 'Brand Safety Assurance',
                id: 'targeting.mediaQualityAssuranceSystem.sections.brandSafetyAssurance'
              })}
              modalConfirm={{
                storageKey: DO_NOT_DISTURB_TARGETING_SECTION_KEY
              }}
              onSectionEnabledChange={(enabled: boolean) => handleSectionEnableChange('brandSafety', enabled)}
              tooltipChildComponent={
                <span>
                  {formatMessage({
                    defaultMessage:
                      'Using machine learning, algorithmic modeling, and human verification. Sonata rates the risk profile of content in the following risk segments using internal and external (IAS) mechanisms.',
                    id: 'targeting.mediaQualityAssuranceSystem.tooltips.brandSafetyAssurance'
                  })}
                </span>
              }
            >
              <div className="mediaQualityAssuranceSystem__sectionContent">
                <div className="mediaQualityAssuranceSystem__selectors">
                  <Selector
                    className="mediaQualityAssuranceSystem__selectorsSimple"
                    id="brandSafetyAssuranceSelectorSimple"
                    label={formatMessage({
                      defaultMessage: 'Safety Level',
                      id: 'targeting.mediaQualityAssuranceSystem.safetyLevel'
                    })}
                    onChange={(val: { id: string }) =>
                      setMediaQualityProps('brandSafety', {
                        ...mediaQuality.brandSafety,
                        assurance: val.id
                      })
                    }
                    options={safetyLevels}
                    tooltipContent={
                      <table className="mediaQualityAssuranceSystem__tooltipTable" style={{ maxWidth: '380px' }}>
                        <thead>
                          <tr>
                            <th>
                              {formatMessage({
                                defaultMessage: 'Level',
                                id: 'targeting.mediaQualityAssuranceSystem.tooltips.levelHeader'
                              })}
                            </th>
                            <th>
                              {formatMessage({
                                defaultMessage: 'Description',
                                id: 'targeting.mediaQualityAssuranceSystem.tooltips.descriptionHeader'
                              })}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td>
                              {formatMessage({
                                defaultMessage: 'Moderate',
                                id: 'targeting.mediaQualityAssuranceSystem.options.moderate'
                              })}
                            </td>
                            <td>
                              {formatMessage({
                                defaultMessage:
                                  'Typically acceptable for brands. However, caution needs to be given to subjective nature of content (for example, alcohol or tobacco).',
                                id: 'targeting.mediaQualityAssuranceSystem.tooltips.moderateBrandSafetyAssuranceDescription'
                              })}
                            </td>
                          </tr>
                          <tr>
                            <td>
                              {formatMessage({
                                defaultMessage: 'High',
                                id: 'targeting.mediaQualityAssuranceSystem.options.high'
                              })}
                            </td>
                            <td>
                              {formatMessage({
                                defaultMessage:
                                  'Generally acceptable content for all ages an audiences; does not typically contain anything offensive in nature or theme.',
                                id: 'targeting.mediaQualityAssuranceSystem.tooltips.highBrandSafetyAssuranceDescription'
                              })}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    }
                    type="simple"
                    value={safetyLevels.filter(val => val.id === mediaQuality.brandSafety.assurance)}
                  />
                  <Selector
                    className="mediaQualityAssuranceSystem__selectorsMultiple"
                    id="brandSafetyAssuranceSelectorMultiple"
                    label={formatMessage({
                      defaultMessage: 'Not appearing in content related to',
                      id: 'targeting.mediaQualityAssuranceSystem.notAppearing'
                    })}
                    onChange={(opts: { id: string }[]) =>
                      setMediaQualityProps('brandSafety', {
                        ...mediaQuality.brandSafety,
                        providers: [
                          {
                            categories: opts.map(item => item.id),
                            name: 'ias'
                          }
                        ]
                      })
                    }
                    options={notAppearingOptions}
                    type="multiple"
                    value={notAppearingOptions.filter(item =>
                      mediaQuality.brandSafety.providers[0].categories.includes(item.id)
                    )}
                  />
                </div>
              </div>
            </SelectionableSectionWithConfirmation>
          </div>
        )}
        {adFraudPermission && (
          <div className="mediaQualityAssuranceSystem__section">
            <SelectionableSectionWithConfirmation
              id="adFraudDetection"
              isSectionEnabled={mediaQuality.adFraud.enabled}
              label={formatMessage({
                defaultMessage: 'Ad Fraud Detection',
                id: 'targeting.mediaQualityAssuranceSystem.sections.adFraudDetection'
              })}
              modalConfirm={{
                storageKey: DO_NOT_DISTURB_TARGETING_SECTION_KEY
              }}
              onSectionEnabledChange={(enabled: boolean) => handleSectionEnableChange('adFraud', enabled)}
              tooltipChildComponent={
                <span>
                  {formatMessage({
                    defaultMessage:
                      'Using machine learning, algorithmic modeling, and human verification. Sonata detects and blocks fraudulent activities (Ad Fraud) using internal and external (IAS) mechanisms.',
                    id: 'targeting.mediaQualityAssuranceSystem.tooltips.adFraudDetection'
                  })}
                </span>
              }
            >
              <div className="mediaQualityAssuranceSystem__sectionContent">
                <div className="mediaQualityAssuranceSystem__selectors">
                  <div className="mediaQualityAssuranceSystem__selectorsSafetyLevelWrapper">
                    <Selector
                      className="mediaQualityAssuranceSystem__selectorsSimple"
                      id="adFraudDetectionSelectorSimple"
                      label={formatMessage({
                        defaultMessage: 'Safety Level',
                        id: 'targeting.mediaQualityAssuranceSystem.safetyLevel'
                      })}
                      onChange={(val: { id: string }) =>
                        setMediaQualityProps('adFraud', {
                          ...mediaQuality.adFraud,
                          risk: val.id
                        })
                      }
                      options={safetyLevels}
                      tooltipContent={
                        <table className="mediaQualityAssuranceSystem__tooltipTable" style={{ maxWidth: '380px' }}>
                          <thead>
                            <tr>
                              <th>
                                {formatMessage({
                                  defaultMessage: 'Level',
                                  id: 'targeting.mediaQualityAssuranceSystem.tooltips.levelHeader'
                                })}
                              </th>
                              <th>
                                {formatMessage({
                                  defaultMessage: 'Description',
                                  id: 'targeting.mediaQualityAssuranceSystem.tooltips.descriptionHeader'
                                })}
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td className="mediaQualityAssuranceSystem__tooltipTableSecondTitle">
                                {formatMessage({
                                  defaultMessage: 'Moderate',
                                  id: 'targeting.mediaQualityAssuranceSystem.options.moderate'
                                })}
                              </td>
                              <td>
                                {formatMessage({
                                  defaultMessage:
                                    'This will act on pages/apps that have been rated to be moderately suspicious of AdFraud techniques.',
                                  id: 'targeting.mediaQualityAssuranceSystem.tooltips.moderateAdFraudRiskDescription'
                                })}
                              </td>
                            </tr>
                            <tr>
                              <td className="mediaQualityAssuranceSystem__tooltipTableSecondTitle">
                                {formatMessage({
                                  defaultMessage: 'High',
                                  id: 'targeting.mediaQualityAssuranceSystem.options.high'
                                })}
                              </td>
                              <td>
                                {formatMessage({
                                  defaultMessage:
                                    'This will act on pages/apps that have been rated to be highly suspicious of AdFraud techniques.',
                                  id: 'targeting.mediaQualityAssuranceSystem.tooltips.highAdFraudRiskDescription'
                                })}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      }
                      type="simple"
                      value={[safetyLevels.find(item => item.id === mediaQuality.adFraud.risk)!]}
                    />
                  </div>
                </div>
              </div>
            </SelectionableSectionWithConfirmation>
          </div>
        )}
        {viewabilityThresholdPermission && (
          <div className="mediaQualityAssuranceSystem__section">
            <SelectionableSectionWithConfirmation
              id="viewabilityTargetingSegment"
              isSectionEnabled={mediaQuality.viewabilityPrebid.enabled}
              label={formatMessage({
                defaultMessage: 'Viewability Targeting Segment System - PRE BID',
                id: 'targeting.mediaQualityAssuranceSystem.sections.viewabilityTargetingSegment'
              })}
              modalConfirm={{
                storageKey: DO_NOT_DISTURB_TARGETING_SECTION_KEY
              }}
              onSectionEnabledChange={(enabled: boolean) => handleSectionEnableChange('viewabilityPrebid', enabled)}
              tooltipChildComponent={
                <span>
                  {formatMessage({
                    defaultMessage:
                      'The Pre-Bid targeting system enables an inventory filter based that allows the user to query both a proprietary ranking system as well as the Integral Ad Science (IAS) Content Rating Platform. This provides valuable information about historical viewability regarding a particular URL (Desktop/Mobile Web) or Bundle ID (Mobile App).',
                    id: 'targeting.mediaQualityAssuranceSystem.tooltips.viewabilityTargetingSegment'
                  })}
                </span>
              }
            >
              {viewabilityAdvanceThresholdPermission ? (
                <div className="mediaQualityAssuranceSystem__sectionContent">
                  <div className="mediaQualityAssuranceSystem__selectors">
                    <Selector
                      className="mediaQualityAssuranceSystem__selectorsSimple"
                      id={'viewabilityTargetingSegmentSelectorSimple'}
                      label={formatMessage({
                        defaultMessage: 'Global Viewability Segment',
                        id: 'targeting.mediaQualityAssuranceSystem.globalViewability'
                      })}
                      onChange={(val: { id: string }) =>
                        setMediaQualityProps('viewabilityPrebid', {
                          ...mediaQuality.viewabilityPrebid,
                          thresholds: {
                            ...mediaQuality.viewabilityPrebid.thresholds,
                            global: val.id
                          }
                        })
                      }
                      options={segmentsOptions}
                      type="simple"
                      value={[
                        segmentsOptions.find(item => item.id === mediaQuality.viewabilityPrebid.thresholds.global)!
                      ]}
                    />
                    <div className="mediaQualityAssuranceSystem__checks">
                      {unknownIncludedPermission && (
                        <>
                          <Checkbox
                            active={targeting.unknownIncluded.viewabilityIasThreshold}
                            id={'viewabilityTargetingSegmentCheckIasUnknown'}
                            labelText={formatMessage({
                              defaultMessage: 'Include IAS Unknown',
                              id: 'targeting.mediaQualityAssuranceSystem.checks.iasUnknown'
                            })}
                            onClickAction={(_id: string, checked: boolean) =>
                              setUnknownIncluded('viewabilityIasThreshold', checked)
                            }
                          />
                          <Checkbox
                            active={targeting.unknownIncluded.viewabilityThreshold}
                            id={'viewabilityTargetingSegmentCheckIabUnknown'}
                            labelText={formatMessage({
                              defaultMessage: 'Include IAB Unknown',
                              id: 'targeting.mediaQualityAssuranceSystem.checks.iabUnknown'
                            })}
                            onClickAction={(_id: string, checked: boolean) =>
                              setUnknownIncluded('viewabilityThreshold', checked)
                            }
                          />
                        </>
                      )}
                      <div className="mediaQualityAssuranceSystem__checksItem">
                        <Checkbox
                          active={mediaQuality.viewabilityPrebid.aboveTheFold}
                          id={'viewabilityTargetingSegmentCheckAboveTheFold'}
                          labelText={formatMessage({
                            defaultMessage: 'Only Ads displayed above the fold',
                            id: 'targeting.mediaQualityAssuranceSystem.checks.aboveTheFold'
                          })}
                          onClickAction={(_id: string, checked: boolean) =>
                            setMediaQualityProps('viewabilityPrebid', {
                              ...mediaQuality.viewabilityPrebid,
                              aboveTheFold: checked
                            })
                          }
                        />
                        <HelpTooltip
                          content={formatMessage({
                            defaultMessage:
                              'Generally acceptable content for all ages an audiences; does not typically contain anything offensive in nature or theme',
                            id: 'targeting.mediaQualityAssuranceSystem.tooltips.highBrandSafetyAssuranceDescription'
                          })}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="mediaQualityAssuranceSystem__set">
                    {formatMessage({
                      defaultMessage: 'Set by Adex:',
                      id: 'targeting.mediaQualityAssuranceSystem.setBy'
                    })}
                  </div>
                  <div className="mediaQualityAssuranceSystem__percentages">
                    <Selector
                      defaultValue={getPreBidValue(0)}
                      id="viewabilityTargetingSegmentSelectorIas"
                      label={formatMessage({
                        defaultMessage: 'IAS',
                        id: 'targeting.mediaQualityAssuranceSystem.selectors.ias'
                      })}
                      onChange={(val: { id: string }) => handlePreBidChange(val.id, 0)}
                      options={PreBidOptions}
                      type="simple"
                    />
                    <Selector
                      defaultValue={getPreBidValue(1)}
                      id="viewabilityTargetingSegmentSelectorDoubleClick"
                      label={formatMessage({
                        defaultMessage: 'DoubleClick',
                        id: 'targeting.mediaQualityAssuranceSystem.selectors.doubleClick'
                      })}
                      onChange={(val: { id: string }) => handlePreBidChange(val.id, 1)}
                      options={PreBidOptions}
                      type="simple"
                    />
                    <Selector
                      defaultValue={getPreBidValue(2)}
                      id="viewabilityTargetingSegmentSelectorAppNexus"
                      label={formatMessage({
                        defaultMessage: 'AppNexus',
                        id: 'targeting.mediaQualityAssuranceSystem.selectors.appNexus'
                      })}
                      onChange={(val: { id: string }) => handlePreBidChange(val.id, 2)}
                      options={PreBidOptions}
                      type="simple"
                    />
                    <Selector
                      defaultValue={getPreBidValue(3)}
                      id="viewabilityTargetingSegmentSelectorOpenX"
                      label={formatMessage({
                        defaultMessage: 'OpenX',
                        id: 'targeting.mediaQualityAssuranceSystem.selectors.openX'
                      })}
                      onChange={(val: { id: string }) => handlePreBidChange(val.id, 3)}
                      options={PreBidOptions}
                      type="simple"
                    />
                    <Selector
                      defaultValue={getPreBidValue(4)}
                      id="viewabilityTargetingSegmentSelectorRubicon"
                      label={formatMessage({
                        defaultMessage: 'Rubicon',
                        id: 'targeting.mediaQualityAssuranceSystem.selectors.rubicon'
                      })}
                      onChange={(val: { id: string }) => handlePreBidChange(val.id, 4)}
                      options={segmentsOptions}
                      type="simple"
                    />
                    <Selector
                      defaultValue={getPreBidValue(5)}
                      id="viewabilityTargetingSegmentSelectorSmaato"
                      label={formatMessage({
                        defaultMessage: 'Smaato',
                        id: 'targeting.mediaQualityAssuranceSystem.selectors.smaato'
                      })}
                      onChange={(val: { id: string }) => handlePreBidChange(val.id, 5)}
                      options={PreBidOptions}
                      type="simple"
                    />
                  </div>
                  {budget && currency && (
                    <GoalPacingDistribution
                      budget={budget}
                      currency={currency}
                      onChange={(value: number) =>
                        setMediaQualityProps('viewabilityPrebid', {
                          ...mediaQuality.viewabilityPrebid,
                          distribution: value
                        })
                      }
                    />
                  )}
                </div>
              ) : undefined}
            </SelectionableSectionWithConfirmation>
          </div>
        )}
        {viewabilityPixelProviderPermission && hasViewabilityPixelProviders && (
          <div className="mediaQualityAssuranceSystem__section">
            <SelectionableSectionWithConfirmation
              id="viewabilityMonitorPixel"
              isSectionEnabled={mediaQuality.viewabilityMeasurement.enabled}
              label={formatMessage({
                defaultMessage: 'Viewability POST BID Monitor Pixel (IAS)',
                id: 'targeting.mediaQualityAssuranceSystem.sections.viewabilityMonitorPixel'
              })}
              modalConfirm={{
                storageKey: DO_NOT_DISTURB_TARGETING_SECTION_KEY
              }}
              onSectionEnabledChange={(enabled: boolean) =>
                handleSectionEnableChange('viewabilityMeasurement', enabled)
              }
              tooltipChildComponent={
                <span>
                  {formatMessage({
                    defaultMessage:
                      'The Post-Bid monitoring IAS pixel collects data in a pixel. This system enables Sonata to provide log-level information on its reporting system. Among other kinds of information, this log-level reporting provides viewability information following the MRC definition of viewability.',
                    id: 'targeting.mediaQualityAssuranceSystem.tooltips.viewabilityMonitorPixel'
                  })}
                </span>
              }
            />
          </div>
        )}
        {attentionMetricsPermission && (
          <div className="mediaQualityAssuranceSystem__section">
            <SelectionableSectionWithConfirmation
              id="attentionRateMeasurement"
              isSectionEnabled={mediaQuality.attentionRateMeasurement?.enabled ?? false}
              label={formatMessage({
                defaultMessage: 'Lumen Attention Measurement',
                id: 'targeting.mediaQualityAssuranceSystem.sections.attentionRateMeasurement'
              })}
              modalConfirm={{
                storageKey: DO_NOT_DISTURB_TARGETING_SECTION_KEY
              }}
              onSectionEnabledChange={(enabled: boolean) =>
                handleSectionEnableChange('attentionRateMeasurement', enabled)
              }
              tooltipChildComponent={
                <span>
                  {formatMessage({
                    defaultMessage:
                      'Lumen Research measures advertising effectiveness based on human visual attention rather than standard viewability. Enabling it lets Sonata report attention metrics for this campaign.',
                    id: 'targeting.mediaQualityAssuranceSystem.tooltips.attentionRateMeasurement'
                  })}
                </span>
              }
            />
          </div>
        )}
      </FormExpandableSection>
    </div>
  );
}

export default MediaQualityAssuranceSystem;
